import React from 'react';
import { SUPPLIERS_QUERY, SupplierListItemType } from '@institutions/graphql';
import { SupplierStatusTypes } from '@common/graphql';
import { useQuery, useNavigate } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { Link, AddButton, ErrorHandler } from '@common/components';
import { formatPhone } from 'works/legacy/services/phone-service';
import { SupplierStatus } from '@institutions/components';
import { normalizeSortingData } from 'works/legacy/lib/table-filters-helper';
import { Colors, FontWeight } from '@common/tokens';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { Table, TableColumnType, TableProps } from 'antd';
import Text from 'antd/lib/typography/Text';
import { buildDialogUrl } from '@common/services';

type SupplierTableProps = TableProps<SupplierListItemType>;

const isPendingActivation = (status: string) => status === 'PENDING_ACTIVATION';

function renderSupplierStatus(
  status: SupplierStatusTypes,
  { id: supplierId }: SupplierListItemType
) {
  if (isPendingActivation(status)) {
    return (
      <div className="action">
        <SupplierStatus status={status} />
        <Link
          to={`${ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE}/activation/${supplierId}`}
        >
          <AddButton>Locations</AddButton>
        </Link>
      </div>
    );
  }

  return <SupplierStatus status={status} />;
}

export function SuppliersManagementList() {
  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();

  const { data, loading, fetchMore, error } = useQuery(SUPPLIERS_QUERY);

  if (error) {
    return <ErrorHandler error={error} log="SuppliersManagementList" />;
  }

  const canShowTiering = currentMembership.canShowTiering;

  function openViewDrawer({ id }: { id: string }) {
    const viewDrawerUrl = buildDialogUrl('supplier-view-drawer', {
      props: { supplierId: id },
    });

    navigate(viewDrawerUrl);
  }

  const handleTableChange: SupplierTableProps['onChange'] = (
    _pagination,
    filters,
    sortingData
  ) => {
    fetchMore({
      variables: {
        input: {
          orders: {
            ...normalizeSortingData(sortingData),
          },
          filters,
        },
      },
      updateQuery: (_prev, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  const tableColumns: TableColumnType<SupplierListItemType>[] = [
    {
      key: 'supplierName',
      dataIndex: ['name'],
      title: 'Supplier',
      onCell: (record) => ({
        onClick: () => openViewDrawer(record),
      }),
      render: (name) => (
        <div className="highlight-column">
          <span className="fillet" />
          {name}
        </div>
      ),
    },
    {
      key: 'phone',
      dataIndex: ['phoneNumber'],
      title: 'Phone Number',
      className: 'phone-number',
      render: (value) => formatPhone(value) || 'N/A',
    },
    {
      key: 'defaultContactName',
      dataIndex: ['defaultContact', 'name'],
      title: 'Main Contact',
      render: (value) => value || 'N/A',
    },
    {
      key: 'defaultContactEmail',
      dataIndex: ['defaultContact', 'email'],
      title: 'E-mail',
      render: (value) => value || 'N/A',
    },
    ...(canShowTiering
      ? [
          {
            key: 'tier',
            dataIndex: ['tierForCurrentOrganization', 'name'],
            title: 'Tier',
            render: (value: string) =>
              value || (
                <Text
                  style={{
                    color: Colors.midnightLightest,
                    fontWeight: FontWeight.light,
                  }}
                  italic
                >
                  No Tier
                </Text>
              ),
          },
        ]
      : []),
    {
      key: 'status',
      dataIndex: ['statusForCurrentOrganization', 'status'],
      title: 'Status',
      render: renderSupplierStatus,
      sorter: true,
      defaultSortOrder: 'ascend',
      width: '25%',
    },
  ];

  return (
    <>
      <section>
        <Table
          className="suppliers-list"
          rowKey="id"
          columns={tableColumns}
          dataSource={data?.suppliers}
          loading={loading}
          pagination={false}
          rowClassName={({ statusForCurrentOrganization }) =>
            statusForCurrentOrganization &&
            isPendingActivation(statusForCurrentOrganization.status)
              ? 'highlight-row'
              : 'supplier-list-row--clickable'
          }
          onRow={(supplier) => ({
            onClick: () => {
              const status = supplier.statusForCurrentOrganization?.status;
              if (status && !isPendingActivation(status)) {
                openViewDrawer(supplier);
              }
            },
          })}
          onChange={handleTableChange}
        />
      </section>
      <style jsx>{`
        :global(.suppliers-list tr > td:first-child) {
          cursor: pointer;
        }

        :global(.suppliers-list .ant-table-tbody > tr > td) {
          padding: 20px 4px 20px 8px;
        }

        :global(.suppliers-list .ant-table-tbody .highlight-column) {
          color: ${Colors.elm};
          font-weight: bold;
        }

        :global(.suppliers-list .ant-table-tbody > .highlight-row) {
          font-weight: bold;
          position: relative;
        }

        :global(
          .suppliers-list .ant-table-tbody .highlight-row .highlight-column
        ) {
          margin-left: 4px;
        }

        :global(.suppliers-list .ant-table-tbody .fillet) {
          width: 4px;
          height: 100%;
          background-color: ${Colors.elm};
          position: absolute;
          left: 0;
          top: 0;
          visibility: hidden;
        }

        :global(.suppliers-list .ant-table-tbody .highlight-row .fillet) {
          visibility: visible;
        }

        :global(.suppliers-list .ant-table-tbody .highlight-row .action) {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-right: 8px;
        }

        :global(
          .suppliers-list .ant-table-tbody .supplier-list-row--clickable
        ) {
          cursor: pointer;
        }

        :global(.suppliers-list .ant-btn) {
          margin-top: -6px;
          margin-bottom: -6px;
        }

        :global(.suppliers-list .phone-number) {
          max-width: 100px;
        }
      `}</style>
    </>
  );
}
